import React from 'react';
import styled from 'styled-components';

const BadgeContainer = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: ${props => props.theme.colors.background};
  border: 1px solid ${props => props.theme.colors.border};
  color: ${props => props.theme.colors.text};
  
  ${props => {
    switch (props.$level) {
      case 'poser':
        return `
          background: #6B7280;
          color: white;
          border-color: #4B5563;
        `;
      case 'scene_supporter':
        return `
          background: #3B82F6;
          color: white;
          border-color: #2563EB;
        `;
      case 'active_member':
        return `
          background: #10B981;
          color: white;
          border-color: #059669;
        `;
      case 'trusted_poster':
        return `
          background: #FEF3C7;
          color: #92400E;
          border-color: #F59E0B;
        `;
      case 'scene_insider':
        return `
          background: #8B5CF6;
          color: white;
          border-color: #7C3AED;
        `;
      case 'scene_veteran':
        return `
          background: #EF4444;
          color: white;
          border-color: #DC2626;
        `;
      case 'scene_legend':
        return `
          background: linear-gradient(135deg, #FEF3C7, #FDE68A);
          color: #92400E;
          border-color: #F59E0B;
          font-weight: 700;
        `;
      default:
        return `
          background: #6B7280;
          color: white;
          border-color: #4B5563;
        `;
    }
  }}
`;

const StatusIcon = styled.div`
  font-size: ${props => props.$size === 'large' ? '24px' : props.$size === 'medium' ? '16px' : '12px'};
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${props => props.$size === 'large' ? '48px' : props.$size === 'medium' ? '32px' : '16px'};
  height: ${props => props.$size === 'large' ? '48px' : props.$size === 'medium' ? '32px' : '16px'};
  border-radius: ${props => props.$size === 'large' ? '50%' : '4px'};
  background: ${props => {
    switch (props.$level) {
      case 'poser':
        return '#6B7280';
      case 'scene_supporter':
        return '#3B82F6';
      case 'active_member':
        return '#10B981';
      case 'trusted_poster':
        return '#FEF3C7';
      case 'scene_insider':
        return '#8B5CF6';
      case 'scene_veteran':
        return '#EF4444';
      case 'scene_legend':
        return 'linear-gradient(135deg, #FEF3C7, #FDE68A)';
      default:
        return '#6B7280';
    }
  }};
  border: ${props => props.$size === 'large' ? '2px solid' : '1px solid'} ${props => {
    switch (props.$level) {
      case 'poser':
        return '#4B5563';
      case 'scene_supporter':
        return '#2563EB';
      case 'active_member':
        return '#059669';
      case 'trusted_poster':
        return '#F59E0B';
      case 'scene_insider':
        return '#7C3AED';
      case 'scene_veteran':
        return '#DC2626';
      case 'scene_legend':
        return '#F59E0B';
      default:
        return '#4B5563';
    }
  }};
`;

const getLevelDisplay = (level) => {
  switch (level) {
    case 'poser':
      return 'Poser';
    case 'scene_supporter':
      return 'Scene Supporter';
    case 'active_member':
      return 'Active Member';
    case 'trusted_poster':
      return 'Trusted Poster';
    case 'scene_insider':
      return 'Scene Insider';
    case 'scene_veteran':
      return 'Scene Veteran';
    case 'scene_legend':
      return 'Scene Legend';
    default:
      return 'Poser';
  }
};

const getLevelIcon = (level) => {
  switch (level) {
    case 'poser':
      return '🎭';
    case 'scene_supporter':
      return '🎵';
    case 'active_member':
      return '🎸';
    case 'trusted_poster':
      return '⭐';
    case 'scene_insider':
      return '🎪';
    case 'scene_veteran':
      return '🔥';
    case 'scene_legend':
      return '👑';
    default:
      return '🎭';
  }
};

// Separate StatusIcon component for use as profile picture
export const StatusIconComponent = ({ level, size = 'medium' }) => {
  const icon = getLevelIcon(level);
  
  return (
    <StatusIcon $level={level} $size={size}>
      {icon}
    </StatusIcon>
  );
};

const StatusBadge = ({ level, showIcon = true, size = 'small' }) => {
  const displayText = getLevelDisplay(level);
  const icon = getLevelIcon(level);
  
  return (
    <BadgeContainer $level={level} $size={size}>
      {showIcon && <span>{icon}</span>}
      {displayText}
    </BadgeContainer>
  );
};

export default StatusBadge; 